import { SaveFormDataRecoordinacion } from "@/erp/orden-servicio/orden-servicio-traslado/components/save-agendamiento-orden-traslado/SaveAgendamientoOrdenTraslado";
import { EstadosAgendamientoEnumChoice, EstadosLlamadaEnumChoice, handleErrorForm, ROUTER_PATHS, toHour } from "@/shared";
import { CustomButtunsRejectConfirm, LoaderComponent, SingleFormBoxScene } from "@/shared/components/ui";
import { Agendamiento, CacheKeyCalendarioENUM, Contrato } from "@/shared/interfaces";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import AgendamientoSingleOrdenServicioVisita from "./AgendamientoSingleOrdenServicioVisita";
import RejectRecoordinadoModal from "@/erp/noc/agendamientonoc/shared/components/forms/RejectRecoordinadoModal";
import { AproveRecoordinacionData, getContrato, useFetchUbicacionProductoSimple, useUpdateAgendamiento } from "@/shared/actions/erp";
import { conceptosSinStock } from "../otros-conceptos/OtrosConceptosVisita";
import { trazaTipoInstalacion } from "../tipo-instalacion/useTiposInstalacion";
import { useAuthStore } from "@/store/auth";
import useSelectDateTimerPreScheduling, { DepartamentoSelectScheduling } from "@/erp/sale/component/calendario-preagendamiento-venta/useSelectDateTimerPreScheduling.store";
import { useFlotasStore } from "@/store/erp";
import { toast } from "react-toastify";
import { ordenServicioVisitaSchema } from "../../utils";

export type SaveAgendamientoOrdenVisitaProps = {
    title: string;
    agendamiento?: Agendamiento,
    enableGestionVenta?: boolean
}
const returnUrlAgendamientosPyLPage = ROUTER_PATHS.operaciones.visitasPylNav
export default function SaveAgendamientoOrdenVisita({
    title,
    agendamiento,
    enableGestionVenta
}: SaveAgendamientoOrdenVisitaProps) {
    const navigate = useNavigate()
    const user = useAuthStore(s => s.user)
    const [dataContrato, setDataContrato] = useState<Contrato | null>(null)
    const [shrink] = useState(true)
    const [openRechazoModal, setOpenRechazoModal] = useState(false)
    //store
    const getFloat = useSelectDateTimerPreScheduling(s => s.getFloat)
    const setPreventa = useSelectDateTimerPreScheduling(s => s.setPreventa)
    const unblockPlanification = useSelectDateTimerPreScheduling(s => s.unblockPlanification);
    const setConfigParams = useSelectDateTimerPreScheduling(s => s.setConfigParams);
    //
    const blocked = useSelectDateTimerPreScheduling(s => s.blocked)
    const selectFloat = useSelectDateTimerPreScheduling(s => s.selectFloat)
    const selectDate = useSelectDateTimerPreScheduling(s => s.selectDate)
    const slot = useSelectDateTimerPreScheduling(s => s.slot)

    const { getFlotasPreagendamiento } = useFlotasStore();
    // mutations 
    const updatePreagendamientoMutation = useUpdateAgendamiento<AproveRecoordinacionData>({})

    // Stock por producto en la ubicación de la flota (solo visitas): para bloquear
    // el guardado si un concepto no tiene stock suficiente. Se valida solo si hay
    // flota asignada y el stock ya cargó (mapa vacío por "no cargado" != stock 0).
    const _ubicOp = (agendamiento?.flota_data as any)?.ubicacion;
    const { data: _invOp, isLoading: _invOpLoading } = useFetchUbicacionProductoSimple({
        enabled: !!_ubicOp,
        params: { page: 1, page_size: 500, estado: true, filterByState: false, es_para_venta: true, ubicacion: _ubicOp } as any,
    });
    const _stockOp = useMemo(() => {
        const m: Record<string, number> = {};
        const arr = (_invOp as any)?.data?.data || [];
        for (const up of arr) {
            const idp = up?.producto_data?.id_producto || up?.id;
            if (idp) m[idp] = Number(up?.cantidad ?? 0);
        }
        return m;
    }, [_invOp]);

    const determinarNuevoEstadoAgendamiento = (data: SaveFormDataRecoordinacion, agendamiento: Agendamiento, fechaNueva?: string, horaNueva?: string) => {
        if (data.estado_llamada === EstadosLlamadaEnumChoice.NO_CONTESTA) {
            return EstadosAgendamientoEnumChoice.EN_PAUSA
        }
        if (data.isRejecting) {
            return EstadosAgendamientoEnumChoice.PRERECHAZADO
        }
        const fechaOriginal = agendamiento.fecha_visita;
        const horaOriginal = agendamiento.hora_visita;
        const newFecha = fechaNueva ?? data.fecha_visita;
        const newHora = horaNueva ?? data.hora_visita;
        const datesModified = fechaOriginal !== newFecha || horaOriginal != newHora;
        if (datesModified) {
            return EstadosAgendamientoEnumChoice.RECORDINADO;
        }
        return EstadosAgendamientoEnumChoice.FINALIZADO
    }
    //** form 
    const form = useForm<SaveFormDataRecoordinacion>({
        resolver: yupResolver(ordenServicioVisitaSchema) as any,
        defaultValues: {
            estado: true
        },
    });
    //moutn cargar calendario y tecnicos disponibles
    useEffect(() => {
        setConfigParams({
            time: 60 * 1000,
            tiempo: CacheKeyCalendarioENUM.TIEMPO_LIMITE_PYL,
            motivo: CacheKeyCalendarioENUM.MENSAJE_BLOQUEO_PYL,
            departamento: DepartamentoSelectScheduling.PYL
        })

        if (!agendamiento) return;

        (async () => {
            await Promise.all([
                getFloat({
                    estado: true,
                    zonas: agendamiento?.preventa_data?.zona,
                    page_size: 100,
                }),
                setPreventa(agendamiento?.preventa_data?.id_preventa!)
            ]);
        })();

        return () => {
            unblockPlanification()
        }

    }, [agendamiento?.id_agendamiento])
    //mount carga flotas
    useEffect(() => {
        getFlotasPreagendamiento({
            zonas: agendamiento?.contrato_data?.zona,
            page_size: 100,
        })
    }, [])
    //mount carga la data
    useEffect(() => {
        if (!agendamiento) return
        async function fetch() {
            const resp = await getContrato(agendamiento?.contrato_data.id_contrato!)
            setDataContrato(resp?.data!)
        }
        fetch()

    }, [agendamiento?.id_agendamiento])
    //mount setea los datos a form
    useEffect(() => {
        if (!agendamiento?.id_agendamiento) return;

        const {
            preventa_data,
            prospecto_data,
            contrato_data,
            plan_data,
            //trazo_venta_data,
            ...rest
        } = agendamiento;

        form.reset({
            ...prospecto_data,
            ...contrato_data,
            ...rest,
            planLabel: plan_data?.nombre!,
            velocidad_internet: preventa_data?.velocidad
                ? `${preventa_data?.velocidad} ${plan_data?.unidad}`
                : "",
            detalle_instalacion: agendamiento?.preagendamiento_data?.detalle_instalacion || "",
            flotaRaw: agendamiento?.flota_data || {},
            fecha_instalacion: agendamiento?.fecha_instalacion || "",
            hora_instalacion: agendamiento?.hora_instalacion || "",
            observacion_llamada:
                agendamiento.observaciones?.observacion_llamada || "",
            otros_conceptos:
                (agendamiento.observaciones as any)?.otros_conceptos || [],
            tipo_instalacion:
                (agendamiento.observaciones as any)?.tipo_instalacion || null,
            coordenadas: agendamiento.coordenadas,
            nap: agendamiento?.nap || "",
            distancia: agendamiento?.distancia || "",
            //valor_instalacion:plan_data.valor || ""
        } as any);
    }, [agendamiento, form.reset]);

    // Usar la identificación/tipo del CLIENTE (base de tickets, cédula válida),
    // no la del prospecto (que puede traer una cédula dummy/inválida).
    useEffect(() => {
        const cli = (dataContrato as any)?.cliente_data;
        if (!cli) return;
        if (cli.identificacion) form.setValue("identificacion", cli.identificacion);
        if (cli.tipo_identificacion) form.setValue("tipo_identificacion", cli.tipo_identificacion);
    }, [dataContrato]);

    const onSave = async (data: SaveFormDataRecoordinacion) => {
        if (!agendamiento?.id_agendamiento) return;
        // Bloqueo por stock (solo visitas, solo si hay flota y el stock cargó).
        if (_ubicOp && !_invOpLoading) {
            const sinStock = conceptosSinStock((data as any).otros_conceptos, _stockOp);
            if (sinStock.length) {
                toast.error(
                    `Sin stock suficiente en la flota para: ${sinStock.join(", ")}. Ajusta el concepto o repón inventario.`
                );
                return;
            }
        }
        // valores reales que se enviarán
        const fechaVisita = blocked
            ? selectDate.format("YYYY-MM-DD")
            : data.fecha_visita;
        const horaVisita = blocked
            ? toHour(slot?.hora ?? 0)
            : data.hora_visita!;
        //update
        const updAgend = {
            id: agendamiento?.id_agendamiento!,
            data: {
                celular: data.celular,
                celular_2: data.celular_2,
                estado_llamada: data.estado_llamada,
                observaciones: {
                    ...agendamiento.observaciones,
                    ...agendamiento.preventa_data?.observaciones,
                    observacion_llamada: data.observacion_llamada,
                    observacion_rechazo: data.observacion_rechazo || "",
                    otros_conceptos: (data as any).otros_conceptos || [],
                    tipo_instalacion: (data as any).tipo_instalacion || null,
                    tipo_instalacion_historial: trazaTipoInstalacion(
                        (agendamiento.observaciones as any)?.tipo_instalacion,
                        (data as any).tipo_instalacion,
                        (agendamiento.observaciones as any)?.tipo_instalacion_historial,
                        user?.username || [user?.nombre, user?.apellido].filter(Boolean).join(" ").trim() || "—",
                        "operaciones"
                    ),
                },
                persona_referencia_celular: data.persona_referencia_celular!,
                persona_referencia_nombre: data.persona_referencia_nombre!,
                persona_referencia_parentesco: data.persona_referencia_parentesco,
                //
                estado_agendamiento: determinarNuevoEstadoAgendamiento(data, agendamiento, fechaVisita, horaVisita),
                motivo_rechazo: data.motivo_rechazo || null,
                observacion_rechazo: data.observacion_rechazo || null,
                ///* cambio de flota y hora de instalacion
                flota: blocked ? selectFloat : data.flota,
                fecha_visita: fechaVisita,
                hora_visita: horaVisita
            } as any
        }
        //
        const resp = await updatePreagendamientoMutation.mutateAsync(updAgend)
        if (resp.code == 200) {
            navigate(returnUrlAgendamientosPyLPage);
        }

    }
    //
    const fechaForm = form.watch("fecha_visita")
    const horaForm = form.watch("hora_visita")
    const fechaOriginal = agendamiento?.fecha_visita;
    const horaOriginal = agendamiento?.hora_visita;
    const fechaNueva = blocked ? selectDate?.format("YYYY-MM-DD") : fechaForm;
    const horaNueva = blocked ? toHour(slot?.hora ?? 0) : horaForm;
    const datesModified = fechaOriginal !== fechaNueva || horaOriginal !== horaNueva
    // loader
    if (!!!dataContrato) {
        return <LoaderComponent />
    }

    return (
        <SingleFormBoxScene
            titlePage={title}
            showCustomBtns={true}
            showActionBtns={false}
            num_referencia={`${(dataContrato as any)?.cliente_data?.razon_social ?? agendamiento?.prospecto_data?.razon_social ?? ""}`}
            customBtns={
                <CustomButtunsRejectConfirm
                    onCancel={() => navigate(returnUrlAgendamientosPyLPage)}
                    confirmTextBtn={datesModified ? "Recoordinar " : "Confirmar"}
                    onConfirm={() => {
                        form.handleSubmit(onSave, (err) => {
                            if (err?.estado_llamada || err?.observacion_llamada)
                                return toast.error("El estado de llamada y su observación son requeridos")
                            handleErrorForm(err)
                        })()
                    }}
                    rejectTextBtn="Enviar a Gestión Ventas"
                    showRejectBtn={enableGestionVenta}
                    onReject={() => {
                        setOpenRechazoModal(true);
                        form.reset({
                            ...form.getValues(),
                            isRejecting: true
                        })

                    }}
                    sxContainer={{ pt: 1 }}

                />
            }
        >
            {
                dataContrato && (
                    <>
                        <AgendamientoSingleOrdenServicioVisita
                            form={form as any}
                            shrink={shrink}
                            agendamiento={agendamiento!}
                            contrato={dataContrato}
                        />

                    </>

                )
            }
            {/*    //** DIALOG */}
            <RejectRecoordinadoModal
                open={openRechazoModal}
                onClose={() => setOpenRechazoModal(false)}
                agendamiento={agendamiento!}
                form={form}
                onConfirm={() => {
                    form.handleSubmit(onSave, (err) => {
                        handleErrorForm(err)
                    })()
                }}
            />


        </SingleFormBoxScene>
    )

}